import React, { useState, useEffect } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { EFilingAttorney, EFilingParty } from '@lds-efsp/shared-contracts';
import FormInput from '../../setup/components/FormInput';
import { useApi, API_BASE_URL } from '../../../api/useApi';

interface AccountAttorney {
  id: string;
  barNumber: string;
  barState: string;
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
  firmName?: string;
  email?: string;
  phone?: string;
}

// WOL-344: role values mirroring EFilingAttorneySchema.role enum.
// Defined locally so this file type-checks even when the shared-contracts dist
// is behind (e.g. in the worktree's symlinked node_modules). CI always builds
// shared-contracts before user-portal so the canonical type is always correct
// at compile time in the pipeline.
type AttorneyRole = 'filing' | 'former' | 'substituting' | 'associating';

// WOL-344: extend EFilingAttorney with the new role field added in WOL-344.
// The schema source already has this field; this local augmentation keeps the
// modal type-safe regardless of which dist version node_modules resolves to.
type EFilingAttorneyWithRole = EFilingAttorney & { role?: AttorneyRole };

interface AddAttorneyModalProps {
  brand: BrandConfig;
  county: string;
  parties: EFilingParty[];
  onSave: (attorney: EFilingAttorneyWithRole) => void;
  onClose: () => void;
  existingAttorney?: EFilingAttorneyWithRole;
  /** WOL-344: true when the wizard is in subsequent-filing mode. Shows role
   *  selector (default 'substituting') and address collection for NEW_ATTORNEY. */
  isSubsequent?: boolean;
}

const PLAINTIFF_ROLES = ['PLAIN', 'PET', 'CLAIM', 'XCMPLNT', 'XPLAIN', 'INTPLAIN', 'INTVEN', 'INTPLD'];

const AddAttorneyModal: React.FC<AddAttorneyModalProps> = ({
  brand,
  county,
  parties,
  onSave,
  onClose,
  existingAttorney,
  isSubsequent = false,
}) => {
  const isEditing = !!existingAttorney;
  const { fetchWithAuth } = useApi();

  // Bar search state
  const [barSearch, setBarSearch] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<string | null>(null);

  // Roster state
  const [roster, setRoster] = useState<AccountAttorney[]>([]);
  const [showRoster, setShowRoster] = useState(false);

  // Attorney form fields
  const [barNumber, setBarNumber] = useState(existingAttorney?.barNumber || '');
  const [barState, setBarState] = useState(existingAttorney?.barState || 'CA');
  const [firstName, setFirstName] = useState(existingAttorney?.firstName || '');
  const [middleName, setMiddleName] = useState(existingAttorney?.middleName || '');
  const [lastName, setLastName] = useState(existingAttorney?.lastName || '');
  const [firmName, setFirmName] = useState(existingAttorney?.firmName || '');
  const [email, setEmail] = useState(existingAttorney?.email || '');
  const [phone, setPhone] = useState(existingAttorney?.phone || '');
  const [representedPartyIds, setRepresentedPartyIds] = useState<string[]>(
    existingAttorney?.representedPartyIds || [],
  );
  const [saveToRoster, setSaveToRoster] = useState(!isEditing);

  // WOL-344: role — shown only in subsequent-filing mode.
  // New attorneys default to 'substituting'; GetCase-loaded attorneys (externalId
  // set) default to 'filing'. The envelope builder does NOT read this field —
  // emission is driven by mv.FORMER_ATTORNEY and externalId presence.
  const defaultRole = (): AttorneyRole => {
    if (existingAttorney?.role) return existingAttorney.role;
    if (existingAttorney?.externalId) return 'filing';
    return isSubsequent ? 'substituting' : 'filing';
  };
  const [role, setRole] = useState<AttorneyRole>(defaultRole);

  // WOL-344: address fields — surfaced so buildNewAttorneyMetadataItem can emit
  // a full ContactInformation/MailingAddress block for the substituting attorney
  // (required for cert scenarios where the attorney is not in the court registry).
  // Maps to SelfRepAddressSchema: address1 / city / state / zip.
  const [addrStreet, setAddrStreet] = useState(existingAttorney?.address?.address1 || '');
  const [addrCity, setAddrCity] = useState(existingAttorney?.address?.city || '');
  const [addrState, setAddrState] = useState(existingAttorney?.address?.state || '');
  const [addrZip, setAddrZip] = useState(existingAttorney?.address?.zip || '');

  // Load roster on mount
  useEffect(() => {
    fetchWithAuth(`${API_BASE_URL}/api/efiling/attorneys`)
      .then(res => res.json())
      .then(json => {
        if (json.data) setRoster(json.data);
      })
      .catch(() => {});
  }, [fetchWithAuth]);

  const handleBarSearch = async () => {
    if (!barSearch.trim()) return;
    setSearching(true);
    setSearchResult(null);
    try {
      const params = new URLSearchParams({ barNumber: barSearch, county });
      const res = await fetchWithAuth(`${API_BASE_URL}/api/efiling/attorneys/lookup?${params}`);
      const json = await res.json();
      if (json.data && json.data.length > 0) {
        const att = json.data[0];
        setBarNumber(barSearch);
        if (att.firstName) setFirstName(att.firstName);
        if (att.lastName) setLastName(att.lastName);
        if (att.middleName) setMiddleName(att.middleName);
        if (att.firmName) setFirmName(att.firmName);
        if (att.email) setEmail(att.email);
        if (att.phone) setPhone(att.phone);
        setSearchResult('found');
      } else {
        setBarNumber(barSearch);
        setSearchResult('not-found');
      }
    } catch {
      setBarNumber(barSearch);
      setSearchResult('error');
    } finally {
      setSearching(false);
    }
  };

  const handleSelectFromRoster = (att: AccountAttorney) => {
    setBarNumber(att.barNumber);
    setBarState(att.barState);
    setFirstName(att.firstName);
    setMiddleName(att.middleName || '');
    setLastName(att.lastName);
    setFirmName(att.firmName || '');
    setEmail(att.email || '');
    setPhone(att.phone || '');
    setShowRoster(false);
    setSaveToRoster(false);
  };

  const handleSave = async () => {
    // WOL-344: build address block when any address field is populated.
    // Maps to SelfRepAddressSchema so buildNewAttorneyMetadataItem can emit a
    // full MailingAddress for attorneys not yet in the court registry (NEW_ATTORNEY).
    const hasAddress = !!(addrStreet || addrCity || addrState || addrZip);
    const attorney: EFilingAttorneyWithRole = {
      id: existingAttorney?.id || crypto.randomUUID(),
      externalId: existingAttorney?.externalId,
      barNumber,
      barState,
      firstName,
      middleName: middleName || undefined,
      lastName,
      firmName: firmName || undefined,
      email: email || undefined,
      phone: phone || undefined,
      representedPartyIds,
      // WOL-344: role label — UI display only, not read by envelope builder
      role: isSubsequent ? role : undefined,
      // WOL-344: address — emitted in NEW_ATTORNEY ContactInformation block
      address: hasAddress
        ? {
            country: 'US',
            address1: addrStreet || undefined,
            city: addrCity || undefined,
            state: addrState || undefined,
            zip: addrZip || undefined,
          }
        : existingAttorney?.address,
    };

    // Save to account roster if checked
    if (saveToRoster && barNumber && lastName) {
      try {
        await fetchWithAuth(`${API_BASE_URL}/api/efiling/attorneys`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            barNumber,
            barState,
            firstName,
            middleName: middleName || undefined,
            lastName,
            firmName: firmName || undefined,
            email: email || undefined,
            phone: phone || undefined,
          }),
        });
      } catch {
        // Non-blocking — attorney still added to wizard
      }
    }

    onSave(attorney);
  };

  const toggleParty = (partyId: string) => {
    setRepresentedPartyIds(prev =>
      prev.includes(partyId)
        ? prev.filter(id => id !== partyId)
        : [...prev, partyId],
    );
  };

  const isValid = barNumber && firstName && lastName;
  const plaintiffParties = parties.filter(p => PLAINTIFF_ROLES.includes(p.roleCode));

  const getPartyName = (party: EFilingParty) =>
    party.partyType === 'person'
      ? [party.firstName, party.lastName].filter(Boolean).join(' ')
      : party.organizationName || '';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ borderColor: brand.colors.border }}
      >
        {/* Header */}
        <div
          className="flex justify-between items-center px-6 py-4 border-b"
          style={{ borderColor: brand.colors.border }}
        >
          <h3 className="text-lg font-semibold" style={{ color: brand.colors.text }}>
            {isEditing ? 'Edit Attorney' : 'Add Attorney'}
          </h3>
          <button onClick={onClose} className="text-xl font-light" style={{ color: brand.colors.textLight }}>
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Bar Number Search */}
          {!isEditing && (
            <div className="mb-4 p-3 rounded border" style={{ borderColor: brand.colors.border }}>
              <p className="text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
                Look up by Bar Number
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Enter bar number..."
                  value={barSearch}
                  onChange={e => setBarSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleBarSearch()}
                  className="flex-1 px-3 py-2 rounded border text-sm"
                  style={{ borderColor: brand.colors.border, color: brand.colors.text }}
                />
                <button
                  onClick={handleBarSearch}
                  disabled={searching || !barSearch.trim()}
                  className="px-4 py-2 rounded text-sm"
                  style={{
                    backgroundColor: searching ? brand.colors.border : brand.colors.primary,
                    color: searching ? brand.colors.textMuted : brand.colors.onPrimary,
                    cursor: searching ? 'wait' : 'pointer',
                  }}
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>
              {searchResult === 'found' && (
                <p className="text-xs mt-1 text-green-600">Attorney found — fields auto-filled below.</p>
              )}
              {searchResult === 'not-found' && (
                <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
                  Not found in court registry. Enter details manually below.
                </p>
              )}

              {/* Roster quick-select */}
              {roster.length > 0 && (
                <div className="mt-2">
                  <button
                    onClick={() => setShowRoster(!showRoster)}
                    className="text-xs underline"
                    style={{ color: brand.colors.link }}
                  >
                    {showRoster ? 'Hide' : 'Select from'} saved roster ({roster.length})
                  </button>
                  {showRoster && (
                    <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                      {roster.map(att => (
                        <button
                          key={att.id}
                          onClick={() => handleSelectFromRoster(att)}
                          className="w-full text-left px-2 py-1 rounded text-sm hover:bg-gray-50"
                          style={{ color: brand.colors.text }}
                        >
                          {att.firstName} {att.lastName}
                          <span className="text-xs ml-2" style={{ color: brand.colors.textLight }}>
                            Bar# {att.barNumber}{att.firmName ? ` — ${att.firmName}` : ''}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Attorney fields */}
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="Bar Number"
              name="barNumber"
              value={barNumber}
              onChange={e => setBarNumber(e.target.value)}
              required
            />
            <FormInput
              brand={brand}
              label="Bar State"
              name="barState"
              value={barState}
              onChange={e => setBarState(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="First Name"
              name="firstName"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              required
            />
            <FormInput
              brand={brand}
              label="Middle Name"
              name="middleName"
              value={middleName}
              onChange={e => setMiddleName(e.target.value)}
            />
          </div>
          <FormInput
            brand={brand}
            label="Last Name"
            name="lastName"
            value={lastName}
            onChange={e => setLastName(e.target.value)}
            required
          />
          <FormInput
            brand={brand}
            label="Firm Name"
            name="firmName"
            value={firmName}
            onChange={e => setFirmName(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormInput
              brand={brand}
              label="Email"
              name="email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <FormInput
              brand={brand}
              label="Phone"
              name="phone"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
            />
          </div>

          {/* WOL-344: Role selector — subsequent-filing mode only.
              Display hint stored on EFilingAttorney.role; envelope builder ignores it. */}
          {isSubsequent && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: brand.colors.border }}>
              <p className="text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
                Attorney Role
              </p>
              <select
                value={role}
                onChange={e => setRole(e.target.value as AttorneyRole)}
                className="w-full px-3 py-2 rounded border text-sm"
                style={{ borderColor: brand.colors.border, color: brand.colors.text }}
              >
                <option value="filing">Filing Attorney (on record at court)</option>
                <option value="substituting">Substituting Attorney (new, replaces former)</option>
                <option value="associating">Associating Attorney (joining the case)</option>
                <option value="former">Former Attorney (being replaced)</option>
              </select>
              <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
                This label helps identify the attorney&apos;s role in the filing. It does not
                change the envelope — the Former Attorney metadata dropdown controls emission.
              </p>
            </div>
          )}

          {/* WOL-344: Address fields — shown in subsequent mode so buildNewAttorneyMetadataItem
              can emit a full MailingAddress for attorneys not yet in the court registry.
              Maps to SelfRepAddressSchema (address1 / city / state / zip). */}
          {isSubsequent && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: brand.colors.border }}>
              <p className="text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
                Mailing Address{' '}
                <span className="font-normal text-xs" style={{ color: brand.colors.textLight }}>
                  (required if attorney is not already in the court registry)
                </span>
              </p>
              <FormInput
                brand={brand}
                label="Street"
                name="addrStreet"
                value={addrStreet}
                onChange={e => setAddrStreet(e.target.value)}
              />
              <div className="grid grid-cols-3 gap-4">
                <FormInput
                  brand={brand}
                  label="City"
                  name="addrCity"
                  value={addrCity}
                  onChange={e => setAddrCity(e.target.value)}
                />
                <FormInput
                  brand={brand}
                  label="State"
                  name="addrState"
                  value={addrState}
                  onChange={e => setAddrState(e.target.value)}
                />
                <FormInput
                  brand={brand}
                  label="ZIP"
                  name="addrZip"
                  value={addrZip}
                  onChange={e => setAddrZip(e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Represented Parties */}
          {plaintiffParties.length > 0 && (
            <div className="mt-4 pt-4 border-t" style={{ borderColor: brand.colors.border }}>
              <p className="text-sm font-medium mb-2" style={{ color: brand.colors.text }}>
                Represents (select parties)
              </p>
              <div className="space-y-1">
                {plaintiffParties.map(party => (
                  <label key={party.id} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={representedPartyIds.includes(party.id)}
                      onChange={() => toggleParty(party.id)}
                      style={{ accentColor: brand.colors.primary }}
                    />
                    <span className="text-sm" style={{ color: brand.colors.text }}>
                      {getPartyName(party)}
                      <span className="text-xs ml-1" style={{ color: brand.colors.textLight }}>
                        ({party.roleName})
                      </span>
                    </span>
                  </label>
                ))}
              </div>
              {representedPartyIds.length === 0 && (
                <p className="text-xs mt-1" style={{ color: brand.colors.textLight }}>
                  No parties selected — attorney will represent all filing parties.
                </p>
              )}
            </div>
          )}

          {/* Save to roster */}
          {!isEditing && (
            <div className="mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveToRoster}
                  onChange={e => setSaveToRoster(e.target.checked)}
                  style={{ accentColor: brand.colors.primary }}
                />
                <span className="text-sm" style={{ color: brand.colors.text }}>
                  Save to account roster for future filings
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex justify-end gap-3 px-6 py-4 border-t"
          style={{ borderColor: brand.colors.border }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border"
            style={{ borderColor: brand.colors.border, color: brand.colors.text }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isValid}
            className="px-4 py-2 rounded-lg text-sm"
            style={{
              backgroundColor: isValid ? brand.colors.primary : brand.colors.border,
              color: isValid ? brand.colors.onPrimary : brand.colors.textMuted,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            {isEditing ? 'Update' : 'Add Attorney'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddAttorneyModal;
